import type { FeedLog } from '../types/production'

export type FeedLogFingerprintSource = Pick<FeedLog, 'farmId' | 'productionUnitId' | 'recordedAt'> & Partial<Pick<FeedLog, 'lot' | 'feedType' | 'feedKg'>>

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeDecimalForKey = (value?: string): string => {
  const raw = value?.trim()
  if (!raw) return ''
  const parsed = Number(raw.replace(',', '.'))
  if (!Number.isFinite(parsed)) return raw
  return parsed.toFixed(3)
}

const normalizeRecordedAtForKey = (value: string): string => {
  const trimmed = value.trim()
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed
  return parsed.toISOString()
}

export const buildFeedLogFingerprint = (log: FeedLogFingerprintSource): string => {
  const lot = (log.lot ?? '').trim()
  const feedType = (log.feedType ?? '').trim().toLowerCase()
  const feedKg = normalizeDecimalForKey(log.feedKg)
  const recordedAt = normalizeRecordedAtForKey(log.recordedAt)
  return [log.farmId.trim(), log.productionUnitId.trim(), lot, recordedAt, feedType, feedKg].join('|')
}

export const parseFeedLogFingerprintSource = (value: unknown): FeedLogFingerprintSource | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const candidate = value as Record<string, unknown>
  const farmId = toOptionalTrimmed(candidate.farmId)
  const productionUnitId = toOptionalTrimmed(candidate.productionUnitId)
  const recordedAt = toOptionalTrimmed(candidate.recordedAt)

  if (!farmId || !productionUnitId || !recordedAt) return null

  const feedKg = typeof candidate.feedKg === 'number'
    ? candidate.feedKg.toString()
    : toOptionalTrimmed(candidate.feedKg)

  return {
    farmId,
    productionUnitId,
    recordedAt,
    lot: toOptionalTrimmed(candidate.lot),
    feedType: toOptionalTrimmed(candidate.feedType),
    feedKg
  }
}